import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { Stack, Text, textVariants, vars } from '@etg/wings';
import { DotOutline } from '@phosphor-icons/react';
import { css } from '@eti/styles';
import { duration as durationUtil } from '@eti/utils';
import { Link, useLocation } from 'react-router-dom';
import type { TripSegmentType } from '../../../../models';
import TimelineRow from '../common/TimelineRow';
import TimelineWrapper from '../../../common/TimelineWrapper';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import { useDurationHTML } from '../../../../../../common/time/utils/useDurationHTML';

const timelineRowStyles = (isDisplayedFirst: boolean, isDisplayedLast: boolean) => css`
  background-color: ${vars.colors.surface.primary};
  border-radius: ${isDisplayedFirst ? `${vars.card.borderRadius} ${vars.card.borderRadius}` : '0 0'}
    ${isDisplayedLast ? `${vars.card.borderRadius} ${vars.card.borderRadius}` : '0 0'};
  padding-inline-end: 8px;
`;

const timelineIconStyles = css`
  transform: translateY(-12px);
`;

interface ItineraryTechnicalStopProps {
  isDisplayedFirst?: boolean;
  isDisplayedLast?: boolean;
  technicalStop: TripSegmentType['technicalStops'][number];
}

const ItineraryTechnicalStop = ({
  isDisplayedFirst = false,
  isDisplayedLast = false,
  technicalStop,
}: ItineraryTechnicalStopProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { language, timeFormat } = useSiteContext();
  const { pathname } = useLocation();
  const { getDurationHTML } = useDurationHTML();

  // Convert from minutes to milliseconds
  const durationInMilliseconds = technicalStop.duration * 60 * 1000;

  const durationText = durationUtil.getDuration(
    durationInMilliseconds,
    language.code,
    timeFormat,
    ['h', 'm'],
    true,
  );

  const isOrderDetailsPage = pathname.includes(routes.ORDER_DETAILS);
  const isTravelInformationPageEnabled = p('OrderDetails.TravelInformation.Enabled');

  return (
    <TimelineRow
      className={timelineRowStyles(isDisplayedFirst, isDisplayedLast)}
      data-testid="itinerary-technical-stop"
    >
      <TimelineWrapper
        borderColorAfter={vars.colors.inputs.border}
        borderColorBefore={vars.colors.inputs.border}
        borderStyleAfter="dashed"
        borderStyleBefore="dashed"
        childSize={8}
        wrapperSize={20}
      >
        <DotOutline
          aria-hidden
          className={timelineIconStyles}
          color="#737373"
          data-testid="itinerary-technical-stop-circle"
          size={32}
          weight="regular"
        />
      </TimelineWrapper>
      <Stack>
        <Text asHtml data-testid="itinerary-technical-stop-heading" variant={textVariants._14_BOLD}>
          {t(
            'TripDetails.Details.TechnicalStop.Heading',
            getDurationHTML(
              durationText,
              durationInMilliseconds,
              'itinerary-technical-stop-duration',
            ),
            `<span data-testid="itinerary-technical-stop-city">${technicalStop.location.cityName}</span>`,
            `<span data-testid="itinerary-technical-stop-location-code">${technicalStop.location.code}</span>`,
          )}
        </Text>
        {!isOrderDetailsPage && (
          <Text asHtml>{t('TripDetails.Details.TechnicalStop.Description')}</Text>
        )}
        {isOrderDetailsPage && (
          <Stack spacing={0}>
            <Text>{t('TripDetails.Details.TechnicalStop.OrderDetails.Description')}</Text>
            {isTravelInformationPageEnabled && (
              <Link to={`${routes.ORDER_DETAILS_TRAVEL_INFORMATION}#TECHNICAL_STOP`}>
                {t('TripDetails.Details.TechnicalStop.OrderDetails.Link.Text')}
              </Link>
            )}
          </Stack>
        )}
      </Stack>
    </TimelineRow>
  );
};

export default ItineraryTechnicalStop;
